import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencyInput } from "@/components/ui/currency-input";
import { PayoutAvatar } from "@/components/admin/payouts/primitives";
import { CircleNotch, FloppyDisk, Warning } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { throwIfNotOk } from "@/lib/api-error";
import type { SubscriptionSettingsView, TherapistSubscriptionView } from "@/types";

interface Props {
  therapist: TherapistSubscriptionView;
  onClose: () => void;
}

const activeUntil = (therapist: TherapistSubscriptionView): string | null => {
  if (!therapist.latestExpiresAt) return null;
  return new Date(therapist.latestExpiresAt).getTime() > Date.now()
    ? therapist.latestExpiresAt
    : null;
};

export function ManualEntryDialog({ therapist, onClose }: Props) {
  const qc = useQueryClient();
  const { data: settings } = useQuery<SubscriptionSettingsView>({
    queryKey: ["admin", "subscription-settings"],
    queryFn: async () => {
      const res = await fetch("/api/admin/subscription-settings");
      await throwIfNotOk(res, "Greška pri učitavanju postavki pretplate.");
      return res.json();
    },
  });

  const [planType, setPlanType] = useState<"semiannual" | "annual" | "custom">("annual");
  const [startsAt, setStartsAt] = useState(new Date().toISOString().slice(0, 10));
  const [expiresAt, setExpiresAt] = useState("");
  const [amountGross, setAmountGross] = useState<number | null>(null);
  const [vatPercent, setVatPercent] = useState("");
  const [vatIncluded, setVatIncluded] = useState(true);
  const [notes, setNotes] = useState("");
  const [generatePdfReceipt, setGeneratePdfReceipt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touchedAmount, setTouchedAmount] = useState(false);

  // Prefill from the configured price list rather than baked-in constants, so a
  // change in "Konfiguracija naplate" does not leave this form quoting an old
  // price. Only until the admin edits a field, so refetches never clobber input.
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!settings) return;
    // The VAT fields are seeded once. Re-applying them on every plan change
    // would silently undo the admin's own edit to the checkbox.
    if (!prefilled) {
      setVatPercent(String(Math.round(settings.vatRate * 10000) / 100));
      setVatIncluded(settings.vatIncluded);
      setPrefilled(true);
    }
    if (!touchedAmount) {
      setAmountGross(planType === "semiannual" ? settings.priceSemiannual : settings.priceAnnual);
    }
  }, [settings, planType, touchedAmount, prefilled]);

  const stacksOnto = planType === "custom" ? null : activeUntil(therapist);
  const missingCustomExpiry = planType === "custom" && !expiresAt;
  const expiryBeforeStart =
    planType === "custom" && !!expiresAt && expiresAt <= startsAt;

  // Typed as a percent here; the schema checks the stored rate (0-1). Without
  // this, "170" became 1.7 and came back as an unrelated rate error.
  const vatNumber = Number(vatPercent.trim().replace(",", "."));
  const vatError =
    vatPercent.trim() === ""
      ? "Upišite PDV stopu u procentima (npr. 17)."
      : !Number.isFinite(vatNumber) || vatNumber < 0 || vatNumber > 100
        ? "PDV stopa mora biti broj od 0 do 100 (procenat, npr. 17)."
        : null;

  async function handleSubmit() {
    if (vatError) {
      setError(vatError);
      return;
    }
    if (!notes.trim()) {
      setError("Upišite napomenu.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/therapists/${therapist.therapistId}/subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planType,
          startsAt,
          expiresAt: planType === "custom" ? expiresAt : undefined,
          amountGross: amountGross,
          vatRate: vatNumber / 100,
          vatIncluded,
          notes: notes.trim(),
          generatePdfReceipt,
        }),
      });
      await throwIfNotOk(res, "Greška pri spremanju pretplate.");
      const result = (await res.json()) as { emailFailed?: boolean };
      qc.invalidateQueries({ queryKey: ["admin", "subscription-overview"] });
      qc.invalidateQueries({ queryKey: ["admin", "subscription-ledger"] });
      if (result.emailFailed) {
        setError(
          "Pretplata je sačuvana, ali slanje računa na email nije uspjelo. Nemojte ponavljati unos, račun pošaljite ručno."
        );
        return;
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={FloppyDisk}
          title="Dodaj pretplatu ručno"
          description="Koristite za migracije ili dogovore van sistema."
          tone="warning"
        />
      </DialogHeader>

      <DialogBody className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-xl bg-muted/40 px-3.5 py-3">
          <PayoutAvatar name={therapist.name} tone="primary" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">
              {therapist.name}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {therapist.payoutMethod === "invoice" ? "Firma (faktura)" : "Fizičko lice (ugovor)"}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-3.5 py-3 text-xs leading-snug text-amber-900 ring-1 ring-inset ring-amber-200/70">
          <Warning className="mt-0.5 size-4 shrink-0" />
          <p>
            Koristi samo za migraciju ili dogovore van sistema. Ručni unos se
            evidentira u listi uplata sa oznakom "Ručno".
          </p>
        </div>

        {!therapist.billingComplete && (
          <div className="flex items-start gap-2.5 rounded-xl bg-rose-50 px-3.5 py-3 text-[13px] leading-snug text-rose-800 ring-1 ring-inset ring-rose-200/70">
            <Warning className="mt-0.5 size-4 shrink-0" />
            <p>
              Terapeut nema popunjene podatke za naplatu (naziv, adresa, JIB ili
              JMBG). Popunite ih prije ručnog unosa.
            </p>
          </div>
        )}

        {stacksOnto && (
          <div className="flex items-start gap-2.5 rounded-xl bg-sky-50 px-3.5 py-3 text-[13px] leading-snug text-sky-900 ring-1 ring-inset ring-sky-200/70">
            <Warning className="mt-0.5 size-4 shrink-0" />
            <p>
              Terapeut je već plaćen do {formatDate(stacksOnto)}. Novi period se
              nadovezuje na taj datum, ne počinje od datuma početka. Za tačne
              datume koristite plan "Ručno".
            </p>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2.5 rounded-xl bg-rose-50 px-3.5 py-3 text-[13px] leading-snug text-rose-800 ring-1 ring-inset ring-rose-200/70">
            <Warning className="mt-0.5 size-4 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tip plana</Label>
              <Select
                value={planType}
                onValueChange={(v) => setPlanType(v as "semiannual" | "annual" | "custom")}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="semiannual">Polugodišnja</SelectItem>
                  <SelectItem value="annual">Godišnja</SelectItem>
                  <SelectItem value="custom">Ručno (custom datumi)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Iznos (bruto)</Label>
              <CurrencyInput
                suffix={settings?.currency ?? "BAM"}
                value={amountGross}
                onChange={(v) => {
                  setTouchedAmount(true);
                  setAmountGross(v);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Datum početka</Label>
              <Input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            {planType === "custom" && (
              <div className="space-y-1.5">
                <Label>Datum isteka</Label>
                <Input
                  type="date"
                  value={expiresAt}
                  min={startsAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                {expiryBeforeStart && (
                  <p className="text-xs text-rose-700">
                    Mora biti poslije datuma početka.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>PDV stopa (%)</Label>
              <Input
                inputMode="decimal"
                value={vatPercent}
                onChange={(e) => setVatPercent(e.target.value)}
                placeholder="17"
              />
              {vatPercent.trim() !== "" && vatError && (
                <p className="text-xs text-rose-700">{vatError}</p>
              )}
            </div>
            <Label className="items-end pb-2 font-normal">
              <Checkbox
                checked={vatIncluded}
                onCheckedChange={(c) => setVatIncluded(c === true)}
              />
              Iznos uključuje PDV
            </Label>
          </div>
          <div className="space-y-1.5">
            <Label>Napomena (obavezno)</Label>
            <Input
              value={notes}
              maxLength={1000}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="npr. Migracija sa starog sistema, plaćeno 1.8.2025."
            />
          </div>
          <Label className="font-normal">
            <Checkbox
              checked={generatePdfReceipt}
              onCheckedChange={(c) => setGeneratePdfReceipt(c === true)}
            />
            Generiši PDF račun i pošalji email terapeutu
          </Label>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Otkaži</Button>
        <Button
          onClick={handleSubmit}
          disabled={
            submitting ||
            !notes.trim() ||
            amountGross === null ||
            amountGross <= 0 ||
            !therapist.billingComplete ||
            missingCustomExpiry ||
            expiryBeforeStart ||
            vatError !== null
          }
        >
          {submitting ? (
            <>
              <CircleNotch className="size-4 animate-spin" />
              Spremam...
            </>
          ) : (
            <>
              <FloppyDisk className="size-4" />
              Sačuvaj unos
            </>
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
